import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Link as LinkIcon, Megaphone, Leaf, Building2, Bug, CloudCog } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { PLATFORMS } from "@/constants/testIds";

const META = {
  chaintwin: {
    Icon: LinkIcon,
    image: "https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg",
    accent: "from-[#007AFF] to-[#00F0FF]",
  },
  mashhoor: {
    Icon: Megaphone,
    image: "https://images.pexels.com/photos/267350/pexels-photo-267350.jpeg",
    accent: "from-[#7C4DFF] to-[#00F0FF]",
  },
  agriai: {
    Icon: Leaf,
    image: "https://images.pexels.com/photos/8316303/pexels-photo-8316303.jpeg",
    accent: "from-[#00E5A0] to-[#00F0FF]",
  },
  urbanai: {
    Icon: Building2,
    image: "https://images.pexels.com/photos/33175694/pexels-photo-33175694.jpeg",
    accent: "from-[#007AFF] to-[#7C4DFF]",
  },
  pestai: {
    Icon: Bug,
    image: "https://customer-assets.emergentagent.com/job_venture-studio-ksa/artifacts/lx3capx8_Image_20260712013955.jpg",
    accent: "from-[#00E5A0] to-[#7C4DFF]",
  },
  hydration: {
    Icon: CloudCog,
    image: "https://customer-assets.emergentagent.com/job_venture-studio-ksa/artifacts/ujx5i121_pole%20%281%29.webp",
    accent: "from-[#00F0FF] to-[#00E5A0]",
  },
};

export default function Platforms() {
  const { t } = useLang();
  const items = t.platforms.items;

  return (
    <section
      id="platforms"
      data-testid={PLATFORMS.root}
      className="relative py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#00F0FF]">
              {t.platforms.eyebrow}
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tighter text-white">
              {t.platforms.title}
            </h2>
          </div>
          <p className="max-w-md text-base text-[#8B95A5] leading-relaxed">
            {t.platforms.sub}
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p, i) => {
            const meta = META[p.key];
            if (!meta) return null;
            const Icon = meta.Icon;
            return (
              <motion.article
                key={p.key}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                data-testid={PLATFORMS.card(p.key)}
                className="glass-card glass-card-hover group relative overflow-hidden rounded-2xl flex flex-col"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={meta.image}
                    alt={p.name}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050810] via-[#050810]/50 to-transparent" />
                  <div className={`absolute inset-0 opacity-40 mix-blend-overlay bg-gradient-to-br ${meta.accent}`} />
                  <div className="absolute top-4 left-4 rtl:left-auto rtl:right-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/40 px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase text-white/80 backdrop-blur">
                    <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-r ${meta.accent}`} />
                    {p.tag}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${meta.accent} text-[#050810]`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <h3 className="font-display text-xl md:text-2xl text-white tracking-tight">
                      {p.name}
                    </h3>
                  </div>
                  <p className="mt-4 text-sm text-[#8B95A5] leading-relaxed flex-1">
                    {p.desc}
                  </p>
                  <a
                    href="#contact"
                    data-testid={PLATFORMS.learn(p.key)}
                    className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-white/80 hover:text-[#00F0FF] transition-colors"
                  >
                    {t.platforms.learn}
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
